import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useFastenConnections from '../../../../../hooks/api/useFastenConnections';
import useFastenRefresh from '../../../../../hooks/api/useFastenRefresh';
import useFastenRefreshHistory from '../../../../../hooks/api/useFastenRefreshHistory';
import { getIntl } from '../../../../../utils/intl';
import LabelValueDisplay, { LabelValueItem } from '../../../../v2BaseComponents/LabelValueDisplay';
import getDateString from '../../../../../utils/getDateString';
import SkeletonLoadingContainer from '../../../../v2BaseComponents/SkeletonLoadingContainer';
import { usePromptButton } from '../../../../shared/PromptButton';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import HeaderContainer from '../../../../v2BaseComponents/HeaderContainer';
import TitledSection from '../../../../v2BaseComponents/TitledSection';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import Text from '../../../../v2BaseComponents/Text';
import { View, Image } from 'react-native';
import DeleteConfirmationPromptContents from './DeleteConfirmationPromptContents';
import ReconnectModal, { useReconnectFlow } from './ReconnectModal';
import MedicalProfileUsageIndicator from '../../../../shared/MedicalProfileUsageIndicator';
import HelpPrompt from '../../../../shared/HelpPrompt';
import { ApiError } from 'aws-amplify/api';
import useNotify from '../../../../../hooks/useNotify';
import { useMedicalProfileUsageMetric } from '../../../../../hooks/api/useMedicalProfileUsageMetric';
import { APITypes, MedicalDataTypes } from '../../../../../primary-record-types';
import { pluralResourceCategoryMessages, resourceCategoryMessages } from '../../../../../utils/sharedMessages';
import { getFastenLogoUrl, formatPlatformName } from '../../../../../utils/fastenUtils';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';

const intl = getIntl('fasten-portal-connection-details', {
    connectionDetails: 'Connection Details',
    disconnectFromPortal: 'Disconnect from Fasten Portal?',
    logoAlt: 'Health System Logo',
    disconnectPortal: 'Disconnect',
    connectionId: 'Connection ID',
    platformType: 'Platform',
    connectedDate: 'Connected',
    connectionStatus: 'Status',
    brandId: 'Brand ID',
    noConnectionFound: 'Connection not found',
    institutionName: 'Institution',
    institutionWebsite: 'Website',
    refreshData: 'Refresh Data',
    refreshing: 'Refreshing...',
    lastRefreshed: 'Last Refreshed',
    refreshAvailableIn: 'Refresh available in {minutes} min',
    never: 'Never',
    refreshLimitReached: "You've used all your refreshes for this billing cycle.",
    refreshLimitReachedButton: 'Refresh Limit Reached',
    initialDataImport: 'Initial data import in progress...',
    refreshError: 'Something went wrong while refreshing your data. Please try again.',
    refreshNotPermitted:
        "You don't have permission to refresh this portal. Ask the profile owner for edit access to patient portals.",
    reconnect: 'Reconnect',
    fastenHealthConnection: 'Fasten Health Connection',
    history: 'History',
    noHistory: 'No history found yet.  It may take up to 3 hours for your data to be available.',
    statusExpired: 'Expired',
    statusRejected: 'Rejected',
    apiMode: 'API mode',
    sandbox: 'Sandbox',
    connectionType: 'Connection type',
    tefca: 'TEFCA',
    tefcaNetwork: 'TEFCA network',
    helpTitle: 'Connection History',
    helpContent: `The counts of records updated with each refresh of a portal.

While most health care systems respond with the full data in under a minute, some systems can take more than an hour.  This means that if you refresh the portal later in the same day, you may get a partial set of data.`,
});

// Status row labels. Map keys mirror the backend connectionStatus enum; `revoked` deliberately
// reads "Expired". Unmapped statuses fall back to the title-cased raw value.
const STATUS_LABEL_KEYS = { revoked: 'statusExpired', rejected: 'statusRejected' } as const;

// Statuses the patient can only clear by re-authorizing with the portal.
const RECONNECTABLE_STATUSES = ['revoked', 'rejected'];

// Helper to format status for display
const formatStatus = (status: string) => {
    if (!status) return 'Unknown';
    const labelKey = STATUS_LABEL_KEYS[status as keyof typeof STATUS_LABEL_KEYS];
    return labelKey ? intl(labelKey) : status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

/**
 * The refresh route answers 403 for two unrelated reasons: the monthly usage allowance is spent, or the caller
 * lacks edit access to this profile's patient portals. Only the usage-allowance check opts into propagating its
 * message to the client, so a 403 carrying that message is a spent allowance and any other 403 is an access denial.
 */
const isUsageAllowanceError = (error: ApiError) => !!error.response?.body?.includes('Usage allowance');

export type FastenPortalConnectionScreenOwnProps = {};

export type FastenPortalConnectionScreenProps = FastenPortalConnectionScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'fasten-portal-connection'>;
};

const HeaderImage: React.FC<{ brandId: string }> = ({ brandId }) => {
    const headerImageSize = 80;
    const logoUrl = getFastenLogoUrl(brandId);

    return (
        <View
            style={{
                width: headerImageSize,
                height: headerImageSize,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f9f9f9',
                borderRadius: 8,
            }}
        >
            <Image
                source={{ uri: logoUrl }}
                resizeMode="contain"
                style={{
                    width: headerImageSize - 8,
                    height: headerImageSize - 8,
                }}
                accessibilityLabel={intl('logoAlt')}
            />
        </View>
    );
};

const FastenPortalConnectionScreen: React.FC<FastenPortalConnectionScreenProps> = (props) => {
    const { orgConnectionId, profileId, autoReconnect } = props.route.params;
    const recordEvent = useRecordEvent();
    const { navigate } = useProfilesTabNavigation();
    const navigateToConnections = React.useCallback(() => {
        navigate('connect', { profileId });
    }, [navigate, profileId]);

    const {
        isLoading,
        connections,
        disconnectConnection,
        isDisconnecting,
        refetch: refetchConnections,
    } = useFastenConnections({
        medicalProfileId: profileId,
        onDisconnectSuccess: navigateToConnections,
    });

    const connection = useMemo(() => {
        return connections.find((c) => c.orgConnectionId === orgConnectionId) || null;
    }, [connections, orgConnectionId]);

    // Refresh hook
    const notify = useNotify();
    const { hasReachedLimit } = useMedicalProfileUsageMetric({
        medicalProfileId: profileId,
        metric: 'fasten-refresh',
    });
    // Refreshing, reconnecting and disconnecting all require edit access to patient portals server-side.
    const hasPortalEditPermission = useHasPermission(profileId, 'patientPortals', 'edit');

    const { canRefresh, isRefreshing, isRefreshPending, triggerRefresh, retryAfterSeconds } = useFastenRefresh({
        medicalProfileId: profileId,
        orgConnectionId,
        enabled: !!connection,
        onRefreshSuccess: () => {
            recordEvent('refreshFastenPortal');
            refetchConnections();
        },
        onRefreshError: (error: Error) => {
            if (error instanceof ApiError && error.response?.statusCode === 403) {
                notify(
                    'error',
                    isUsageAllowanceError(error) ? intl('refreshLimitReached') : intl('refreshNotPermitted'),
                );
            } else {
                notify('error', intl('refreshError'));
            }
        },
    });

    const refreshButtonDisabled =
        !hasPortalEditPermission || hasReachedLimit || !canRefresh || isRefreshing || isRefreshPending;
    const refreshButtonTitle = useMemo(() => {
        if (hasReachedLimit) {
            return intl('refreshLimitReachedButton');
        }
        if (isRefreshing || isRefreshPending) {
            return intl('refreshing');
        }
        if (!connection?.lastRefreshDateTime && !canRefresh) {
            return intl('initialDataImport');
        }
        if (retryAfterSeconds && !canRefresh) {
            const minutes = Math.ceil(retryAfterSeconds / 60);
            return intl('refreshAvailableIn', { minutes: minutes.toString() });
        }
        return intl('refreshData');
    }, [
        hasReachedLimit,
        isRefreshing,
        isRefreshPending,
        retryAfterSeconds,
        canRefresh,
        connection?.lastRefreshDateTime,
    ]);

    // Refresh history hook
    const { isLoading: isLoadingHistory, history } = useFastenRefreshHistory({
        medicalProfileId: profileId,
        orgConnectionId,
        enabled: !!connection,
    });

    const isTefca = connection?.connectionType === 'tefca';
    const displayName =
        connection?.institutionName || (isTefca ? intl('tefcaNetwork') : formatPlatformName(connection?.platformType));

    const deleteButtonPrompt = usePromptButton({
        renderPromptContents: (onClose) => (
            <DeleteConfirmationPromptContents
                connectionName={displayName}
                onClose={onClose}
                onDisconnect={() => {
                    recordEvent('deleteFastenPortal');
                    disconnectConnection(orgConnectionId);
                }}
                isDisconnecting={isDisconnecting}
            />
        ),
        promptTitle: intl('disconnectFromPortal'),
    });

    // Reconnect flow (modal, auto-reconnect, state management)
    const { handleReconnect, isReconnectModalOpen, setIsReconnectModalOpen, isReconnecting } = useReconnectFlow({
        profileId,
        orgConnectionId,
        autoReconnect,
        connection,
        displayName,
        hasReachedLimit,
        hasEditPermission: hasPortalEditPermission,
    });

    const labelValueItems = useMemo<LabelValueItem[]>(() => {
        if (!connection) return [];

        const items: LabelValueItem[] = [
            ...(connection.platformType
                ? [{ label: intl('platformType'), value: formatPlatformName(connection.platformType) }]
                : []),
            {
                label: intl('connectionStatus'),
                value: formatStatus(connection.connectionStatus),
            },
            {
                label: intl('connectedDate'),
                value: connection.date ? getDateString(connection.date, 'date-time') : '',
            },
            {
                label: intl('lastRefreshed'),
                value: connection.lastRefreshDateTime
                    ? getDateString(connection.lastRefreshDateTime, 'date-time')
                    : intl('never'),
            },
            {
                label: intl('connectionId'),
                value: connection.orgConnectionId,
            },
        ];

        // Add website if available
        if (connection.institutionWebsite) {
            items.splice(1, 0, {
                label: intl('institutionWebsite'),
                value: connection.institutionWebsite,
            });
        }

        if (connection.apiMode === 'test') {
            items.push({
                label: intl('apiMode'),
                value: intl('sandbox'),
            });
        }

        if (connection.connectionType === 'tefca') {
            items.push({
                label: intl('connectionType'),
                value: intl('tefca'),
            });
        }

        return items;
    }, [connection]);

    const getHistoryLabeledValue = (
        item: APITypes.ehrIntegrationsService.FastenDataPullHistoryEntry | null,
    ): LabelValueItem => {
        const getResourceCountsText = () => {
            if (!item)
                return new Array(4)
                    .fill(null)
                    .map(() => 'Loading...')
                    .join(', ');

            const categories: MedicalDataTypes.PrimaryRecordResourceCategory[] = [
                'allergy',
                'condition',
                'medication',
                'visit',
                'lab',
                'imaging',
                'vaccination',
                'procedure',
            ];

            const counts = categories
                .map((category) => {
                    const count = item?.resourceCounts[category] || 0;
                    const categoryLabel =
                        count === 1 ? resourceCategoryMessages(category) : pluralResourceCategoryMessages(category);
                    return count > 0 ? `${count} ${categoryLabel}` : null;
                })
                .filter(Boolean);
            return counts.join(', ');
        };

        return {
            label: item?.dateTime ? getDateString(item.dateTime, 'date-time') : '',
            value: getResourceCountsText(),
            isLoading: !item,
        };
    };

    const placeholderHistoryLabeledValue = (
        <LabelValueDisplay
            omitEmptyValues={false}
            items={[getHistoryLabeledValue(null), getHistoryLabeledValue(null), getHistoryLabeledValue(null)]}
        />
    );

    return (
        <>
            <AnimateLoadedContent isLoading={isLoading}>
                {() =>
                    connection ? (
                        <>
                            <HeaderContainer>
                                <Stack vertical justify={'center'} align={'center'} space={3}>
                                    {connection.brandId ? <HeaderImage brandId={connection.brandId} /> : null}
                                    <Stack vertical justify="center" align="center">
                                        <View>
                                            <Text variant="h4" alignText="center">
                                                {displayName}
                                            </Text>
                                        </View>
                                        <View>
                                            <Text variant="body-1" alignText="center" color="Gray-Text">
                                                {intl('fastenHealthConnection')}
                                            </Text>
                                        </View>
                                    </Stack>
                                </Stack>
                            </HeaderContainer>
                            <ActionButtonLayout
                                whiteBackground
                                scrollable
                                actions={
                                    RECONNECTABLE_STATUSES.includes(connection.connectionStatus)
                                        ? [
                                              {
                                                  icon: 'RefreshCw',
                                                  title: hasReachedLimit
                                                      ? intl('refreshLimitReachedButton')
                                                      : intl('reconnect'),
                                                  onPress: handleReconnect,
                                                  isDisabled: !hasPortalEditPermission || hasReachedLimit,
                                                  // A TEFCA connection is re-established through Fasten's widget.
                                                  show: hasPortalEditPermission && !isTefca,
                                              },
                                              {
                                                  icon: 'Trash2',
                                                  title: intl('disconnectPortal'),
                                                  onPress: deleteButtonPrompt.onPress,
                                                  isDisabled: !hasPortalEditPermission || isDisconnecting,
                                                  show: hasPortalEditPermission,
                                              },
                                          ]
                                        : [
                                              {
                                                  icon: 'RefreshCw',
                                                  title: refreshButtonTitle,
                                                  onPress: triggerRefresh,
                                                  isDisabled: refreshButtonDisabled,
                                                  show: hasPortalEditPermission,
                                              },
                                              {
                                                  icon: 'Trash2',
                                                  title: intl('disconnectPortal'),
                                                  onPress: deleteButtonPrompt.onPress,
                                                  isDisabled: !hasPortalEditPermission || isDisconnecting,
                                                  show: hasPortalEditPermission,
                                              },
                                          ]
                                }
                            >
                                <MainContentContainer>
                                    <ContentStack>
                                        <TitledSection title={intl('connectionDetails')}>
                                            <MainContentContainer>
                                                <SkeletonLoadingContainer isLoading={isLoading}>
                                                    <LabelValueDisplay
                                                        items={labelValueItems}
                                                        omitEmptyValues={!isLoading}
                                                    />
                                                </SkeletonLoadingContainer>
                                            </MainContentContainer>
                                        </TitledSection>
                                        <TitledSection
                                            title={intl('history')}
                                            headerRight={
                                                <Stack horizontal align="center" space={4}>
                                                    <MedicalProfileUsageIndicator
                                                        medicalProfileId={profileId}
                                                        metricType="fasten-refresh"
                                                    />
                                                    <HelpPrompt
                                                        title={intl('helpTitle')}
                                                        message={intl('helpContent')}
                                                    />
                                                </Stack>
                                            }
                                        >
                                            <MainContentContainer>
                                                {isLoadingHistory ? (
                                                    placeholderHistoryLabeledValue
                                                ) : history?.length ? (
                                                    <LabelValueDisplay
                                                        items={history
                                                            .sort(
                                                                (e1, e2) =>
                                                                    new Date(e2.dateTime).getTime() -
                                                                    new Date(e1.dateTime).getTime(),
                                                            )
                                                            .map((item) => getHistoryLabeledValue(item))}
                                                    />
                                                ) : (
                                                    <Text variant="subtitle-2">{intl('noHistory')}</Text>
                                                )}
                                            </MainContentContainer>
                                        </TitledSection>
                                    </ContentStack>
                                </MainContentContainer>
                            </ActionButtonLayout>
                        </>
                    ) : (
                        <MainContentContainer>
                            <Stack vertical justify="center" align="center" space={4}>
                                <Text variant="h4" alignText="center">
                                    {intl('noConnectionFound')}
                                </Text>
                            </Stack>
                        </MainContentContainer>
                    )
                }
            </AnimateLoadedContent>
            {deleteButtonPrompt.Prompt}

            <ReconnectModal
                connection={connection}
                displayName={displayName}
                isOpen={isReconnectModalOpen}
                setIsOpen={setIsReconnectModalOpen}
                isReconnecting={isReconnecting}
            />
        </>
    );
};

export default React.memo(FastenPortalConnectionScreen);
